import { SetPlacar } from '../types/placar';

/**
 * Interface para o resultado da validação de um set individual
 */
export interface ValidacaoSetPlacarResult {
  gamesA: boolean;
  gamesB: boolean;
  tiebreakA: boolean;
  tiebreakB: boolean;
  numeroSet: number;
  erros: string[];
}

/**
 * Interface para o resultado da validação do placar completo
 */
export interface ValidacaoPlacarResult {
  valido: boolean;
  mensagem: string;
  camposInvalidos: ValidacaoSetPlacarResult[];
}

/**
 * Interface mínima para CategoriaTorneio usada pelo validador
 */
interface CategoriaTorneioMinima {
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
}

/**
 * Interface mínima para Partida usada pelo validador
 */
export interface PartidaMinima {
  categoriaTorneio: CategoriaTorneioMinima;
  numeroSets?: number | null;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  placar?: {
    sets?: SetPlacar[] | null;
  } | null;
  /**
   * QA 2026-06-24: quando true (WO/desistência), a regra "sem empate / vencer por 2"
   * é pulada — o placar parcial de uma desistência pode estar empatado.
   */
  permitirEmpateParcial?: boolean;
}

/**
 * Classe para validação de placar de beach tennis
 */
export class BeachTennisScoreValidator {
  /**
   * Valida se um valor é considerado vazio (null, undefined ou string vazia)
   * Nota: 0 não é considerado vazio, pois pode ser um valor válido
   */
  private static ehVazio(valor: number | string | null | undefined): boolean {
    return valor === null || valor === undefined || valor === '';
  }

  /**
   * Normaliza um valor numérico ou string para número ou null
   */
  private static normalizarNumero(valor: number | string | null | undefined): number | null {
    if (this.ehVazio(valor)) return null;
    if (typeof valor === 'string') {
      const num = Number(valor);
      return isNaN(num) ? null : num;
    }
    return valor as number;
  }

  /**
   * Constrói mensagem de erro baseada nos campos inválidos
   */
  private static construirMensagemErro(camposInvalidos: ValidacaoSetPlacarResult[]): string {
    if (camposInvalidos.length === 0) {
      return '';
    }
    
    const partesMensagem: string[] = [];
    
    for (const campoInvalido of camposInvalidos) {
      const numeroSet = campoInvalido.numeroSet;
      const erros = campoInvalido.erros;
      
      if (erros.length > 0) {
        const errosFormatados = erros.map(erro => `  - ${erro}`).join('\n');
        partesMensagem.push(`Set ${numeroSet}:\n${errosFormatados}`);
      } else {
        // Caso onde não há erros específicos (ex: Super Tiebreak inválido)
        partesMensagem.push(`Set ${numeroSet}: Verifique os valores preenchidos.`);
      }
    }
    
    return `O placar contém erros:\n${partesMensagem.join('\n\n')}`;
  }

  /**
   * Verifica se um set deve ter tiebreak preenchido
   * Baseado nas regras de validação:
   * - Regra 2: Se tiebreaks for vazio, não deve ter tiebreak
   * - Regra 3: Se games < tiebreaks, tiebreaks não devem existir (mesmo quando gamesA === gamesB)
   * - Regra 3.5: Quando gamesA === tiebreaks E gamesB === tiebreaks, deve ter tiebreak
   * 
   * @param set - Set do placar
   * @param gamesPorSets - Número de games por set (não usado diretamente, mas mantido para compatibilidade)
   * @param tiebreaks - Valor de games que ativa o tiebreak
   * @returns true se o set deve ter tiebreak preenchido, false caso contrário
   */
  public static deveTerTiebreak(
    set: SetPlacar,
    gamesPorSets: number | null | undefined,
    tiebreaks: number | null | undefined
  ): boolean {
    // Normalizar valores
    const tiebreaksValor = this.normalizarNumero(tiebreaks);
    const gamesAValor = this.normalizarNumero(set.gamesA);
    const gamesBValor = this.normalizarNumero(set.gamesB);

    // Regra 2: Se tiebreaks for vazio, não deve ter tiebreak
    if (tiebreaksValor === null) {
      return false;
    }

    // Se não há games preenchidos, não pode ter tiebreak ainda
    if (gamesAValor === null || gamesBValor === null) {
      return false;
    }

    // Regra 3.5: Quando gamesA === tiebreaks E gamesB === tiebreaks, deve ter tiebreak
    if (gamesAValor === tiebreaksValor && gamesBValor === tiebreaksValor) {
      return true;
    }

    // Regra 3: Tiebreak existe quando ambos games >= tiebreaks
    // Exceção: quando gamesA === gamesB, permitir tiebreak mesmo que games < tiebreaks
    if (gamesAValor >= tiebreaksValor && gamesBValor >= tiebreaksValor) {
      return true;
    }
    if (gamesAValor === gamesBValor) {
      return true;
    }

    // Caso contrário, não deve ter tiebreak
    return false;
  }

  /**
   * Verifica se a partida pode ter Super Tiebreak
   */
  public static podeTerSuperTiebreak(partida: PartidaMinima): boolean {
    // Extrair numeroSets da partida ou categoriaTorneio
    const numeroSets = partida.numeroSets ?? partida.categoriaTorneio.numeroSets ?? null;
    
    // Super Tiebreak só é permitido para 2 ou 4 sets
    if (numeroSets !== 2 && numeroSets !== 4) {
      return false;
    }

    // Se não há placar ou sets, não pode ter Super Tiebreak
    if (!partida.placar || !partida.placar.sets || partida.placar.sets.length === 0) {
      return false;
    }

    // Calcular vitórias de cada equipe (excluindo Super Tiebreak)
    let vitoriasEquipeA = 0;
    let vitoriasEquipeB = 0;

    for (const set of partida.placar.sets) {
      // Ignorar Super Tiebreak (numeroSet > numeroSets)
      if (numeroSets !== null && set.numeroSet > numeroSets) {
        continue;
      }

      const gamesA = this.normalizarNumero(set.gamesA);
      const gamesB = this.normalizarNumero(set.gamesB);
      const tiebreakA = this.normalizarNumero(set.tiebreakA);
      const tiebreakB = this.normalizarNumero(set.tiebreakB);

      if (gamesA === null || gamesB === null) {
        continue;
      }

      // Determinar vencedor do set
      if (gamesA > gamesB) {
        vitoriasEquipeA++;
      } else if (gamesB > gamesA) {
        vitoriasEquipeB++;
      } else if (gamesA === gamesB) {
        // Empate em games - verificar tiebreak
        if (tiebreakA !== null && tiebreakB !== null) {
          if (tiebreakA > tiebreakB) {
            vitoriasEquipeA++;
          } else if (tiebreakB > tiebreakA) {
            vitoriasEquipeB++;
          }
        }
      }
    }

    // Super Tiebreak só é permitido quando a partida está empatada
    return vitoriasEquipeA === vitoriasEquipeB;
  }

  /**
   * Valida um set individual
   */
  private static validarSet(
    set: SetPlacar,
    gamesPorSets: number | null | undefined,
    tiebreaks: number | null | undefined
  ): ValidacaoSetPlacarResult | null {
    const resultado: ValidacaoSetPlacarResult = {
      gamesA: false,
      gamesB: false,
      tiebreakA: false,
      tiebreakB: false,
      numeroSet: set.numeroSet,
      erros: [],
    };

    // Normalizar valores
    const gamesPorSetsValor = this.normalizarNumero(gamesPorSets);
    const tiebreaksValor = this.normalizarNumero(tiebreaks);
    const gamesAValor = this.normalizarNumero(set.gamesA);
    const gamesBValor = this.normalizarNumero(set.gamesB);
    const tiebreakAValor = this.normalizarNumero(set.tiebreakA);
    const tiebreakBValor = this.normalizarNumero(set.tiebreakB);

    const gamesPorSetsVazio = gamesPorSetsValor === null;
    const tiebreaksVazio = tiebreaksValor === null;

    // Regra 1: Se gamesPorSets for vazio, não validar gamesA e gamesB
    // (pular para regra de tiebreaks)

    // Regra 2: Se tiebreaks for vazio, tiebreaks não devem existir
    if (tiebreaksVazio) {
      if (tiebreakAValor !== null) {
        resultado.tiebreakA = true;
        resultado.erros.push(`tiebreaks não deve existir nesta partida.`);
      }
      if (tiebreakBValor !== null) {
        resultado.tiebreakB = true;
        resultado.erros.push(`tiebreaks não deve existir nesta partida.`);  
      }
    }

    // Regra 3: Se games < tiebreaks, tiebreaks não devem existir
    // Exceção: quando gamesA === gamesB (ex.: 6-6), permitir tiebreak mesmo que games < tiebreaks
    // Exemplo: gamesPorSets=6, tiebreaks=7, placar válido: 6-6 com tiebreak 7-5
    if (!tiebreaksVazio && tiebreaksValor !== null) {
      const gamesMenorQueTiebreaks =
        (gamesAValor !== null && gamesAValor < tiebreaksValor) ||
        (gamesBValor !== null && gamesBValor < tiebreaksValor);
      const excecaoEmpate =
        gamesAValor !== null &&
        gamesBValor !== null &&
        gamesAValor === gamesBValor;

      if (gamesMenorQueTiebreaks && !excecaoEmpate) {
        if (tiebreakAValor !== null) {
          resultado.tiebreakA = true;
          resultado.erros.push(`Games deve ter valor '${tiebreaksValor}' para existir tiebreak.`);
        }
        if (tiebreakBValor !== null) {
          resultado.tiebreakB = true;
          resultado.erros.push(`Games deve ter valor '${tiebreaksValor}' para existir tiebreak.`);
        }
      }
    }

    // Regra 3.5: Quando games chegam ao valor do tiebreak, tiebreak deve ser válido
    // Se gamesA === tiebreaks E gamesB === tiebreaks, então tiebreaks devem estar preenchidos (caso contrário, são inválidos)
    // tiebreakA ou tiebreakB devem ter valor maior que 7
    // Exemplo: gamesPorSets=6, tiebreaks=7, placar inválido: 7-7 com tiebreakA=3, tiebreakB=5
    // Exemplo: gamesPorSets=6, tiebreaks=7, placar válido: 7-7 com tiebreakA=7, tiebreakB=5
    if (!tiebreaksVazio && tiebreaksValor !== null && tiebreaksValor > 0) {
      if (gamesAValor !== null && gamesBValor !== null &&
          gamesAValor === tiebreaksValor && gamesBValor === tiebreaksValor) {
        // Quando games chegam ao valor do tiebreak, tiebreaks devem ser válidos
        if (tiebreakAValor == null) {
          resultado.tiebreakA = true;
          resultado.erros.push(`Quando games A e games B são '${tiebreaksValor}', tiebreak A deve ser preenchido.`);
        } 
        if (tiebreakBValor == null) {
          resultado.tiebreakB = true;
          resultado.erros.push(`Quando games A e games B são '${tiebreaksValor}', tiebreak B deve ser preenchido.`);
        } 
        if (tiebreakAValor !== null && tiebreakBValor !== null && tiebreakAValor < 7 && tiebreakBValor < 7) {
          resultado.tiebreakA = true;
          resultado.tiebreakB = true;
          resultado.erros.push(`Quando games A e games B são '${tiebreaksValor}', pelo menos um tiebreak deve ser >= 7.`);
        }
      }
    }

    // Regra 15: Quando tiebreaks > 0, não podemos ter ambos gamesA > tiebreaks e gamesB > tiebreaks
    // Exemplo: gamesPorSets=6, tiebreaks=5, placar inválido: 6-6
    // Exemplo: gamesPorSets=6, tiebreaks=5, placar válido: 6-4 ou 4-6
    if (!tiebreaksVazio && tiebreaksValor !== null && tiebreaksValor > 0) {
      if (gamesAValor !== null && gamesBValor !== null &&
          gamesAValor > tiebreaksValor && gamesBValor > tiebreaksValor) {
        resultado.gamesA = true;
        resultado.gamesB = true;
        resultado.erros.push(`Games A e Games B não podem ser ambos maiores que '${tiebreaksValor}'.`);
      }
    }

    // Validações de games (apenas se gamesPorSets não for vazio)
    if (!gamesPorSetsVazio && gamesPorSetsValor !== null) {
      // Regra 4: Games não podem exceder gamesPorSets + 1
      // Exemplo: gamesPorSets=6, placar válido máximo: 7-6 ou 6-7
      if (gamesAValor !== null && gamesAValor > gamesPorSetsValor + 1) {
        resultado.gamesA = true;
        resultado.erros.push(`Valor de Games não pode exceder '${gamesPorSetsValor + 1}'.`);
      }
      if (gamesBValor !== null && gamesBValor > gamesPorSetsValor + 1) {
        resultado.gamesB = true;
        resultado.erros.push(`Valor de Games não pode exceder '${gamesPorSetsValor + 1}'.`);
      }
      // Regra 5: Ambos os games não podem ser maior que gamesPorSets
      // Exemplo: gamesPorSets=6, placar inválido: 7-7
      if (gamesAValor !== null && gamesBValor !== null && gamesAValor > gamesPorSetsValor && gamesBValor > gamesPorSetsValor) {
        resultado.gamesA = true;
        resultado.gamesB = true;
        resultado.erros.push(`Games A e Games B não podem ser ambos maiores que '${gamesPorSetsValor}'.`);
      }

      // Regra 14: A diferença entre gamesA e gamesB não pode ser 1
      // Exemplo: gamesPorSets=6, placar inválido: 6-5
      // Exemplo: gamesPorSets=6, placar válido: 7-5 ou 6-3
      if (gamesAValor !== null && gamesBValor !== null) {
        if (Math.abs(gamesAValor - gamesBValor) === 1) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`A diferença entre Games A e Games B não pode ser 1.`);
        }
      }

      // Regra 6: Se gamesA < gamesPorSets - 1, gamesB deve ser gamesPorSets
      // Exemplo: gamesPorSets=6, se gamesA=4, então gamesB deve ser 6
      if (gamesAValor !== null && gamesAValor < gamesPorSetsValor - 1) {
        if (gamesBValor !== gamesPorSetsValor) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Se Games A é menor que '${gamesPorSetsValor - 1}', Games B deve ser '${gamesPorSetsValor}'.`);
        }
      }

      // Regra 7: Se gamesB < gamesPorSets - 1, gamesA deve ser gamesPorSets
      // Exemplo: gamesPorSets=6, se gamesB=4, então gamesA deve ser 6
      if (gamesBValor !== null && gamesBValor < gamesPorSetsValor - 1) {
        if (gamesAValor !== gamesPorSetsValor) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Se Games B é menor que '${gamesPorSetsValor - 1}', Games A deve ser '${gamesPorSetsValor}'.`);
        }
      }

      // Regra 8: Se tiebreaks === gamesPorSets e gamesA === gamesPorSets - 1,
      // gamesB deve ser gamesPorSets + 1
      // Exemplo: gamesPorSets=6, tiebreaks=6, placar válido: 5-7 (vai para tiebreak)
      if (!tiebreaksVazio && tiebreaksValor === gamesPorSetsValor &&
          gamesAValor === gamesPorSetsValor - 1) {
        if (gamesBValor !== gamesPorSetsValor + 1) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Se Tiebreaks é '${gamesPorSetsValor}' e Games A é '${gamesPorSetsValor - 1}', Games B deve ser '${gamesPorSetsValor + 1}'.`);
        }
      }

      // Regra 9: Se tiebreaks === gamesPorSets e gamesB === gamesPorSets - 1,
      // gamesA deve ser gamesPorSets + 1
      // Exemplo: gamesPorSets=6, tiebreaks=6, placar válido: 7-5 (vai para tiebreak)
      if (!tiebreaksVazio && tiebreaksValor === gamesPorSetsValor &&
          gamesBValor === gamesPorSetsValor - 1) {
        if (gamesAValor !== gamesPorSetsValor + 1) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Se Tiebreaks é '${gamesPorSetsValor}' e Games B é '${gamesPorSetsValor - 1}', Games A deve ser '${gamesPorSetsValor + 1}'.`);
        }
      }

      // Regra 10: Se tiebreaks === gamesPorSets - 1 e gamesA === gamesPorSets - 1,
      // gamesB deve ser gamesPorSets - 1
      // Exemplo: gamesPorSets=6, tiebreaks=5, placar válido: 5-5 (vai para tiebreak)
      if (!tiebreaksVazio && tiebreaksValor === gamesPorSetsValor - 1 &&
          gamesAValor === gamesPorSetsValor - 1) {
        if (gamesBValor !== gamesPorSetsValor - 1) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Se Tiebreaks é '${gamesPorSetsValor - 1}' e Games A é '${gamesPorSetsValor - 1}', Games B deve ser '${gamesPorSetsValor - 1}'.`);
        }
      }

      // Regra 11: Se tiebreaks === gamesPorSets - 1 e gamesB === gamesPorSets - 1,
      // gamesA deve ser gamesPorSets - 1
      // Exemplo: gamesPorSets=6, tiebreaks=5, placar válido: 5-5 (vai para tiebreak)
      if (!tiebreaksVazio && tiebreaksValor === gamesPorSetsValor - 1 &&
          gamesBValor === gamesPorSetsValor - 1) {
        if (gamesAValor !== gamesPorSetsValor - 1) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Se Tiebreaks é '${gamesPorSetsValor - 1}' e Games B é '${gamesPorSetsValor - 1}', Games A deve ser '${gamesPorSetsValor - 1}'.`);
        }
      }

      // Regra 13: Ambos games não podem ser menores que gamesPorSets
      // EXCETO quando tiebreaks === gamesPorSets - 1 e ambos são gamesPorSets - 1 (caso das Regras 10/11)
      // Exemplo: gamesPorSets=6, placar inválido: 4-4 (ambos menores que 6)
      // Exemplo: gamesPorSets=6, tiebreaks=5, placar válido: 5-5 (caso especial)
      if (gamesAValor !== null && gamesBValor !== null &&
          gamesAValor < gamesPorSetsValor && gamesBValor < gamesPorSetsValor) {
        // Verificar se não é o caso especial das Regras 10/11
        const ehCasoEspecialTiebreak = !tiebreaksVazio && 
          tiebreaksValor === gamesPorSetsValor - 1 &&
          gamesAValor === gamesPorSetsValor - 1 &&
          gamesBValor === gamesPorSetsValor - 1;
        
        if (!ehCasoEspecialTiebreak) {
          resultado.gamesA = true;
          resultado.gamesB = true;
          resultado.erros.push(`Games A e Games B não podem ser ambos menores que '${gamesPorSetsValor}'.`);
        }
      }
    }

    // Regra 12: Validação de valores de tiebreak
    // Quando ambos tiebreaks estão preenchidos, devem seguir regras específicas:
    // - Se são iguais, invalida
    // - Se pelo menos um for = 7, a diferença deve ser maior ou igual a 2
    // - Se pelo menos um for > 7, a diferença deve ser igual a 2
    // - Se ambos forem < 7, invalida
    // Exemplo válido: tiebreakA=7, tiebreakB=2 (diferença >= 2)
    // Exemplo válido: tiebreakA=8, tiebreakB=6 (diferença >= 2)
    // Nota: Super Tiebreak geralmente é jogado até 10 pontos, mas não há validação específica aqui
    if (tiebreakAValor !== null && tiebreakBValor !== null) {
      // Se são iguais, invalida
      if (tiebreakAValor === tiebreakBValor) {
        resultado.tiebreakA = true;
        resultado.tiebreakB = true;
        resultado.erros.push(`Tiebreaks não podem ter o mesmo valor.`);
      }
      // Se pelo menos um for >= 7, verificar se diferença é >= 2
      else if (tiebreakAValor >= 7 || tiebreakBValor >= 7) {
        const diferenca = Math.abs(tiebreakAValor - tiebreakBValor);
        if (diferenca < 2) {
          resultado.tiebreakA = true;
          resultado.tiebreakB = true;
          resultado.erros.push(`A diferença entre Tiebreak A e Tiebreak B deve ser pelo menos 2.`);
        }
        else if (diferenca > 2 && (tiebreakAValor > 7 || tiebreakBValor > 7)) {
          resultado.tiebreakA = true;
          resultado.tiebreakB = true;
          resultado.erros.push(`Quando Tiebreak A ou Tiebreak B maior que 7, a diferença deve ser igual a 2.`);
        }
      }
      // Se ambos forem < 7, invalida
      else {
        resultado.tiebreakA = true;
        resultado.tiebreakB = true;
        resultado.erros.push(`Pelo menos um Tiebreak deve ser maior ou igual a 7.`);
      }
    }


    // Verificar se algum campo foi marcado como inválido
    if (resultado.gamesA || resultado.gamesB || resultado.tiebreakA || resultado.tiebreakB) {
      return resultado;
    }

    return null;
  }

  /**
   * Valida o placar completo de uma partida
   */
  public static validarPlacar(partida: PartidaMinima): ValidacaoPlacarResult {
    // Validar que categoriaTorneio existe
    if (!partida.categoriaTorneio) {
      throw new Error('categoriaTorneio é obrigatório para validação do placar');
    }

    // Extrair valores de configuração
    const numeroSets = partida.numeroSets ?? partida.categoriaTorneio.numeroSets ?? null;
    const gamesPorSets = partida.gamesPorSets ?? partida.categoriaTorneio.gamesPorSets ?? null;
    const tiebreaks = partida.tiebreaks ?? partida.categoriaTorneio.tiebreaks ?? null;

    // Inicializar resultado
    const resultado: ValidacaoPlacarResult = {
      valido: true,
      mensagem: '',
      camposInvalidos: [],
    };

    // Se não há placar ou sets, retornar válido
    if (!partida.placar || !partida.placar.sets || partida.placar.sets.length === 0) {
      return resultado;
    }

    // Validar cada set
    for (const set of partida.placar.sets) {
      // Determinar se é Super Tiebreak
      const ehSuperTiebreak = numeroSets !== null && set.numeroSet === numeroSets + 1;

      if (ehSuperTiebreak) {
        // Validar Super Tiebreak
        const podeTerSuperTiebreak = this.podeTerSuperTiebreak(partida);
        const temValores = 
          (set.gamesA !== null && set.gamesA !== 0) ||
          (set.gamesB !== null && set.gamesB !== 0) ||
          (set.tiebreakA !== null && set.tiebreakA !== 0) ||
          (set.tiebreakB !== null && set.tiebreakB !== 0);

        if (!podeTerSuperTiebreak && temValores) {
          // Marcar todos os campos como inválidos
          resultado.camposInvalidos.push({
            gamesA: true,
            gamesB: true,
            tiebreakA: true,
            tiebreakB: true,
            numeroSet: set.numeroSet,
            erros: [],
          });
        }
      } else {
        // Validar set normal
        const validacaoSet = this.validarSet(set, gamesPorSets, tiebreaks);
        if (validacaoSet) {
          resultado.camposInvalidos.push(validacaoSet);
        }
      }
    }

    // QA 2026-06-24: empate de games proibido; sem tiebreak configurado, vencer por 2
    // (paridade com PlacarService.validarVitoriaPorDois). Só quando NÃO há tiebreak
    // configurado — com tiebreak, as regras de formato (Regra 3.5/14) já cuidam.
    // Pulado em WO/desistência (placar parcial pode estar empatado).
    if (!partida.permitirEmpateParcial && tiebreaks === null) {
      const setsComErro = new Set(resultado.camposInvalidos.map((c) => c.numeroSet));
      for (const set of partida.placar.sets) {
        const ehSuperTiebreak = numeroSets !== null && set.numeroSet === numeroSets + 1;
        if (ehSuperTiebreak || setsComErro.has(set.numeroSet)) {
          continue;
        }
        const ga = this.normalizarNumero(set.gamesA);
        const gb = this.normalizarNumero(set.gamesB);
        const jogado = ga !== null && gb !== null && (ga > 0 || gb > 0);
        if (!jogado) {
          continue;
        }
        const gaN = ga as number;
        const gbN = gb as number;
        const tbA = this.normalizarNumero(set.tiebreakA);
        const tbB = this.normalizarNumero(set.tiebreakB);
        const empateDeGames = gaN === gbN;
        const tiebreakDecisivo = tbA !== null && tbB !== null && tbA !== tbB;
        if (empateDeGames && !tiebreakDecisivo) {
          resultado.camposInvalidos.push({
            gamesA: true,
            gamesB: true,
            tiebreakA: false,
            tiebreakB: false,
            numeroSet: set.numeroSet,
            erros: ['Empate em games não é permitido — registre um tiebreak para definir o vencedor.'],
          });
        } else if (!empateDeGames && Math.abs(gaN - gbN) < 2) {
          resultado.camposInvalidos.push({
            gamesA: true,
            gamesB: true,
            tiebreakA: false,
            tiebreakB: false,
            numeroSet: set.numeroSet,
            erros: ['Sem tiebreak, o set deve terminar com 2 games de diferença (ex.: 6-4, 7-5).'],
          });
        }
      }
    }

    // Se houver campos inválidos, atualizar resultado
    if (resultado.camposInvalidos.length > 0) {
      resultado.valido = false;
      resultado.mensagem = this.construirMensagemErro(resultado.camposInvalidos);
    }

    return resultado;
  }
}

